import { useCallback, useEffect, useState } from 'react';
import { BrandCard, BudsHeader, PageShell } from './buds-header';
import { ConnectionForm } from './connection-form';
import { RewardsPanel } from './rewards-panel';
import { SyncScopeCard } from './sync-scope-card';
import { SyncSettingsCard } from './sync-settings-card';
import { SyncDefaultsCard } from './sync-defaults-card';
import { SyncProductsCard } from './sync-products-card';
import { SyncHowItWorksCard } from './sync-how-it-works-card';
import { SyncCategoriesCard } from './sync-categories-card';
import { callApi } from '@/lib/api';
import { errorCardCopy } from './api-errors';

export type ShopifyDefaultRedemptionMode = 'SHOPIFY_ORDER' | 'SHOPIFY_VOUCHER';
export type ShopifyOrderKind = 'DRAFT' | 'FULL';

export interface SyncDefaultsView {
  redemptionMode: ShopifyDefaultRedemptionMode;
  freeShipping: boolean;
  orderKind: ShopifyOrderKind;
  defaultMarkupPoints: number;
  defaultCategoryId: string | null;
}

export interface StatusView {
  status: 'NOT_CONFIGURED' | 'CONNECTED';
  shopifyShopDomain: string;
  message?: string;
  shopifyShopId?: string;
  storeId?: string;
  partnerId?: string;
  storeName?: string;
  partnerName?: string;
  autoSyncEnabled?: boolean;
  initialSyncCompletedAt?: string | null;
  lastAutoSyncedAt?: string | null;
  lastAutoSyncError?: string | null;
  syncScope?: 'ALL' | 'SELECTED';
  syncDefaults?: SyncDefaultsView;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'retrying' }
  | { kind: 'no-bridge' }
  | { kind: 'error'; error: unknown }
  | { kind: 'ready'; status: StatusView };

export function ConnectionRoot() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [rewardsRefreshToken, setRewardsRefreshToken] = useState(0);
  const [reconnectCatchUpRequired, setReconnectCatchUpRequired] =
    useState(false);

  const load = useCallback(async (isRetry: boolean) => {
    setState({ kind: isRetry ? 'retrying' : 'loading' });
    try {
      const view = await callApi<StatusView>('/v1/shopify-app/status');
      setState({ kind: 'ready', status: view });
    } catch (error) {
      setState({ kind: 'error', error });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('id_token') && !window.shopify) {
      setState({ kind: 'no-bridge' });
      return;
    }
    void load(false);
  }, [load]);

  if (state.kind === 'no-bridge') {
    return (
      <PageShell>
        <BudsHeader subtitle="Rewards for Shopify" />
        <BrandCard title="Open from Shopify">
          <p className="text-sm leading-relaxed text-buds-dark/80">
            Open Buds Rewards from your Shopify admin to use it.
          </p>
        </BrandCard>
      </PageShell>
    );
  }

  if (state.kind === 'error') {
    const copy = errorCardCopy(state.error);
    return (
      <PageShell>
        <BudsHeader subtitle="Rewards for Shopify" />
        <BrandCard title={copy.title}>
          <p className="text-sm leading-relaxed text-buds-dark/80">
            {copy.body}
          </p>
          <button
            type="button"
            onClick={() => void load(true)}
            className="mt-4 rounded-full bg-buds-dark px-4 py-2 text-sm font-semibold text-white transition hover:bg-buds-dark/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-buds-green"
          >
            Try again
          </button>
        </BrandCard>
      </PageShell>
    );
  }

  if (state.kind === 'loading' || state.kind === 'retrying') {
    return (
      <PageShell>
        <BudsHeader subtitle="Rewards for Shopify" />
        <BrandCard>
          <div className="flex flex-col items-center justify-center gap-3 py-6">
            <div
              aria-hidden
              className="h-10 w-10 animate-spin rounded-full border-2 border-buds-green/30 border-t-buds-green"
            />
            <p className="text-sm font-medium text-buds-dark">
              {state.kind === 'retrying' ? 'Retrying…' : 'Connecting to Buds…'}
            </p>
            <p className="text-xs text-buds-dark/60">
              Loading your shop&apos;s status.
            </p>
          </div>
        </BrandCard>
      </PageShell>
    );
  }

  const { status } = state;
  const isConnected = status.status === 'CONNECTED';

  const onConnectionChange = (next: StatusView) => {
    setReconnectCatchUpRequired(
      status.status === 'NOT_CONFIGURED' &&
        next.status === 'CONNECTED' &&
        Boolean(next.initialSyncCompletedAt),
    );
    setState({ kind: 'ready', status: next });
  };
  const onSyncToggle = (autoSyncEnabled: boolean) =>
    setState({ kind: 'ready', status: { ...status, autoSyncEnabled } });
  const onScope = (syncScope: 'ALL' | 'SELECTED') =>
    setState({ kind: 'ready', status: { ...status, syncScope } });
  const onDefaults = (syncDefaults: SyncDefaultsView) =>
    setState({ kind: 'ready', status: { ...status, syncDefaults } });
  const onManualRunFinished = (establishedInitialSync: boolean) => {
    setRewardsRefreshToken((token) => token + 1);
    if (!establishedInitialSync) return;
    setReconnectCatchUpRequired(false);
    setState({
      kind: 'ready',
      status: {
        ...status,
        initialSyncCompletedAt:
          status.initialSyncCompletedAt ?? new Date().toISOString(),
      },
    });
  };
  const onReconnectRequired = (message?: string) => {
    setReconnectCatchUpRequired(false);
    setState({
      kind: 'ready',
      status: {
        status: 'NOT_CONFIGURED',
        shopifyShopDomain: status.shopifyShopDomain,
        message:
          message ??
          'Shopify needs to be reconnected before sync can continue.',
      },
    });
  };

  return (
    <PageShell>
      <BudsHeader subtitle="Rewards for Shopify" />
      <div className="space-y-4">
        <ConnectionForm
          shop={status.shopifyShopDomain}
          initialStatus={status}
          onChange={onConnectionChange}
        />
        {isConnected && (
          <SyncScopeCard
            initialScope={status.syncScope ?? 'SELECTED'}
            onChange={onScope}
            onReconnectRequired={onReconnectRequired}
          />
        )}
        {isConnected && (
          <RewardsPanel
            shop={status.shopifyShopDomain}
            refreshToken={rewardsRefreshToken}
          />
        )}
        {isConnected && (
          <SyncDefaultsCard
            initial={
              status.syncDefaults ?? {
                redemptionMode: 'SHOPIFY_ORDER',
                freeShipping: false,
                orderKind: 'DRAFT',
                defaultMarkupPoints: 0,
                defaultCategoryId: null,
              }
            }
            onChange={onDefaults}
            onReconnectRequired={onReconnectRequired}
          />
        )}
        {isConnected && <SyncHowItWorksCard />}
        {isConnected && (
          <SyncProductsCard
            initialSyncCompleted={Boolean(status.initialSyncCompletedAt)}
            onRunFinished={onManualRunFinished}
            onReconnectRequired={onReconnectRequired}
          />
        )}
        {isConnected && (
          <SyncSettingsCard
            initialEnabled={status.autoSyncEnabled ?? false}
            initialSyncCompleted={Boolean(status.initialSyncCompletedAt)}
            reconnectCatchUpRequired={reconnectCatchUpRequired}
            lastAutoSyncedAt={status.lastAutoSyncedAt ?? null}
            lastAutoSyncError={status.lastAutoSyncError ?? null}
            onChange={onSyncToggle}
            onReconnectRequired={onReconnectRequired}
          />
        )}
        {isConnected && (
          <SyncCategoriesCard onReconnectRequired={onReconnectRequired} />
        )}
      </div>
    </PageShell>
  );
}
